import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../../../../../components/ConfirmModal/ConfirmModal';
import {
  getDailyStatePriorityLevels,
  getKeywordsByBusinessArea,
  createKeyword,
  updateKeyword,
  deleteKeyword,
} from '../../../../../services/dailyStatePriorityService';
import { useSnackbar } from 'context/SnackbarContext';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configAreasI18n } from './i18n';
import './configArea.scss';

const normalize = (value) => (value ?? '').trim().toLowerCase();

const getPriorityColor = (name) => {
  const normalized = normalize(name);

  if (normalized.includes('alta') || normalized.includes('high')) return 'error';
  if (
    normalized.includes('media') ||
    normalized.includes('m\u00e9dia') ||
    normalized.includes('medium')
  ) {
    return 'warning';
  }

  return 'success';
};

export default function AreaKeywordsModal({
  DesignSystemComponents,
  Modal,
  area,
  show,
  userId,
  onClose,
}) {
  const { language } = useLanguage();
  const areasLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configAreasI18n });
  const { showSnackbar } = useSnackbar();

  const { Button, Input, Table, IconButton, Dropdown, Badge } =
    DesignSystemComponents ?? {};

  const [priorityLevels, setPriorityLevels] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inlineMessage, setInlineMessage] = useState(null);
  const [newKeyword, setNewKeyword] = useState('');
  const [newPriorityLevelId, setNewPriorityLevelId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState({
    dailyStateProcess: '',
    priorityLevelId: null,
  });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    configAreasI18n.changeLanguage(areasLanguage);
  }, [areasLanguage]);

  const priorityLevelOptions = useMemo(
    () => (priorityLevels || []).map((p) => ({ label: p.name, value: p.id })),
    [priorityLevels]
  );

  const existingKeywordSet = useMemo(() => {
    return new Set((keywords || []).map((k) => normalize(k.dailyStateProcess)));
  }, [keywords]);

  const resetLocalState = () => {
    setPriorityLevels([]);
    setKeywords([]);
    setInlineMessage(null);

    setNewKeyword('');
    setNewPriorityLevelId(null);

    setEditingId(null);
    setEditValue({ dailyStateProcess: '', priorityLevelId: null });

    setIsDeleteOpen(false);
    setToDelete(null);

    setLoading(false);
    setSaving(false);
  };

  useEffect(() => {
    if (!show || !area?.id) return;

    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setInlineMessage(null);

        const [levels, areaKeywords] = await Promise.all([
          getDailyStatePriorityLevels(),
          getKeywordsByBusinessArea(area.id),
        ]);

        if (!mounted) return;

        setPriorityLevels(levels || []);
        setKeywords(areaKeywords || []);
      } catch (err) {
        console.error('Error loading keywords modal', err);
        if (mounted) {
          setInlineMessage({
            type: 'error',
            text: t('config_areas_keywords_error_load'),
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, area?.id]);

  useEffect(() => {
    if (show) return;
    resetLocalState();
  }, [show]);

  const resetCache = () => {
    sessionStorage.removeItem('dailyStateCache');
    window.dispatchEvent(new Event('dailyStateUpdated'));
  };

  const handleCreate = async () => {
    const kw = normalize(newKeyword);
    if (!kw) {
      setInlineMessage({
        type: 'error',
        text: t('config_areas_keywords_error_empty'),
      });
      return;
    }
    if (!newPriorityLevelId) {
      setInlineMessage({
        type: 'error',
        text: t('config_areas_keywords_error_priority_required'),
      });
      return;
    }
    if (existingKeywordSet.has(kw)) {
      setInlineMessage({
        type: 'error',
        text: t('config_areas_keywords_error_duplicate'),
      });
      return;
    }

    try {
      setSaving(true);
      setInlineMessage(null);

      await createKeyword({
        businessAreaId: area.id,
        dailyStateProcess: newKeyword.trim(),
        priorityLevelId: newPriorityLevelId.value,
        userId,
      });

      resetCache();

      const updated = await getKeywordsByBusinessArea(area.id);
      setKeywords(updated || []);

      setNewKeyword('');
      showSnackbar(t('config_areas_keywords_success_created'), 'success');
      onClose?.();
    } catch (err) {
      console.error('Error creating keyword', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.response?.data ||
        t('config_areas_keywords_error_create');
      setInlineMessage({
        type: 'error',
        text:
          typeof message === 'string'
            ? message
            : t('config_areas_keywords_error_create'),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePriority = async (row, selectedOption) => {
    const nextPriorityLevelId =
      selectedOption?.value ?? selectedOption?.id ?? selectedOption ?? null;
    if (!nextPriorityLevelId) return;
    if (Number(nextPriorityLevelId) === Number(row.priorityLevelId)) return;

    try {
      setSaving(true);
      setInlineMessage(null);
      await updateKeyword(row.id, {
        dailyStateProcess: row.dailyStateProcess,
        priorityLevelId: nextPriorityLevelId,
        userId,
      });

      resetCache();

      const updated = await getKeywordsByBusinessArea(area.id);
      setKeywords(updated || []);
      showSnackbar(t('config_areas_keywords_success_priority_updated'), 'success');
      onClose?.();
    } catch (err) {
      console.error('Error updating keyword priority', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.response?.data ||
        t('config_areas_keywords_error_update_priority');
      setInlineMessage({
        type: 'error',
        text:
          typeof message === 'string'
            ? message
            : t('config_areas_keywords_error_update_priority'),
      });
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (row) => {
    setToDelete(row);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!toDelete?.id) return;

    try {
      setSaving(true);
      setInlineMessage(null);

      await deleteKeyword(toDelete.id, userId);

      resetCache();

      const updated = await getKeywordsByBusinessArea(area.id);
      setKeywords(updated || []);

      setIsDeleteOpen(false);
      setToDelete(null);
      showSnackbar(t('config_areas_keywords_success_deleted'), 'success');
      onClose?.();
    } catch (err) {
      console.error('Error deleting keyword', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.response?.data ||
        t('config_areas_keywords_error_delete');
      setInlineMessage({
        type: 'error',
        text:
          typeof message === 'string'
            ? message
            : t('config_areas_keywords_error_delete'),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!Modal || !show || !area) return null;
  if (!Button || !Input || !Table || !IconButton || !Dropdown || !Badge) {
    return null;
  }

  return (
    <>
      <Modal
        title={t('config_areas_keywords_title', { areaName: area.name })}
        show={show}
        onClose={onClose}
        width='900px'
      >
        {inlineMessage?.text ? (
          <div className='d-flex mb-3'>
            <p className='caption text-danger mb-0'>{inlineMessage.text}</p>
          </div>
        ) : null}

        <div className='row mb-3'>
          <div className='col-6'>
            <Input
              name='newKeyword'
              label={t('config_areas_keywords_label')}
              placeholder={t('config_areas_keywords_placeholder')}
              value={newKeyword}
              onChange={(event) => setNewKeyword(event.target.value)}
              disabled={loading || saving}
            />
          </div>

          <div className='col-4'>
            <Dropdown
              ariaLabel={t('config_areas_keywords_priority_aria')}
              label={t('config_areas_keywords_priority_label')}
              placeholder={t('config_areas_keywords_priority_placeholder')}
              options={priorityLevelOptions}
              value={newPriorityLevelId}
              onSelect={(opt) => setNewPriorityLevelId(opt)}
              disabled={loading || saving}
            />
          </div>

          <div className='col-2 pt-4'>
            <Button
              label={
                saving
                  ? t('config_areas_keywords_saving_button')
                  : t('config_areas_keywords_add_button')
              }
              variant='primary'
              size='sm'
              onClick={handleCreate}
              disabled={loading || saving}
              className='ms-auto'
            />
          </div>
        </div>

        <Table
          columns={[
            {
              accessor: 'dailyStateProcess',
              label: t('config_areas_keywords_col_keyword'),
              render: (row) =>
                editingId === row.id ? (
                  <Input
                    name={`editKeyword-${row.id}`}
                    value={editValue.dailyStateProcess}
                    onChange={(event) =>
                      setEditValue((value) => ({
                        ...value,
                        dailyStateProcess: event.target.value,
                      }))
                    }
                    disabled={saving}
                  />
                ) : (
                  <span>{row.dailyStateProcess}</span>
                ),
            },
            {
              accessor: 'priorityLevelId',
              label: t('config_areas_keywords_col_level'),
              render: (row) => {
                const priorityLevel = priorityLevels.find(
                  (level) => level.id === row.priorityLevelId
                );

                return (
                  <div className='d-flex align-items-center gap-2'>
                    <Badge
                      text={priorityLevel?.name ?? '--'}
                      color={getPriorityColor(priorityLevel?.name)}
                      size='sm'
                    />
                  </div>
                );
              },
            },
            {
              label: t('config_areas_keywords_col_actions'),
              render: (row) => (
                <div className='d-flex'>
                  <Dropdown
                    ariaLabel={t('config_areas_keywords_change_level_aria')}
                    icon='threeDotsVertical'
                    variant='icon'
                    options={priorityLevelOptions}
                    onSelect={(opt) =>
                      handleChangePriority(row, opt?.id ?? opt)
                    }
                    disabled={saving}
                  />
                  <IconButton
                    ariaLabel={t('config_areas_keywords_delete_aria')}
                    icon='trash'
                    onClick={() => requestDelete(row)}
                    size='md'
                    iconStroke='var(--error-500)'
                    disabled={saving}
                  />
                </div>
              ),
            },
          ]}
          data={keywords}
          loading={loading}
        />

        {!loading && keywords.length === 0 && (
          <div className='text-center p-3'>
            <p className='caption font-light mb-0'>
              {t('config_areas_keywords_empty')}
            </p>
          </div>
        )}
      </Modal>

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('config_areas_keywords_confirm_delete_title')}
        message={
          toDelete
            ? t('config_areas_keywords_confirm_delete_message', {
                keyword: toDelete.dailyStateProcess,
              })
            : t('config_areas_keywords_confirm_delete_fallback')
        }
        confirmLabel={t('config_areas_confirm_delete_button')}
        cancelLabel={t('config_areas_cancel')}
        confirmVariant='danger'
        onConfirm={confirmDelete}
        onClose={() => {
          setIsDeleteOpen(false);
          setToDelete(null);
        }}
      />
    </>
  );
}
